import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Package, Sparkles, Search, Loader2, ExternalLink, RefreshCw, X, Heart,
} from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { API, BACKEND_URL } from "../../lib/api";

const PAGE_SIZE = 20;

export function AdminProductsManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("__all__");
  const [sortBy, setSortBy] = useState("newest"); // newest | oldest | name_asc | name_desc
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkJob, setBulkJob] = useState(null); // {job_id, total, done, errors, status, current_name}
  const pollRef = useRef(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data || []);
    } catch (e) {
      toast.error("Erreur de chargement des produits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  // Auto-scroll en haut à l'ouverture d'une fiche produit (édition) — la vue
  // change sans changement de route, donc le ScrollToTop global ne s'applique pas.
  useEffect(() => { if (editing) window.scrollTo({ top: 0, behavior: 'smooth' }); }, [editing]);

  // Liste triée des catégories disponibles
  const categories = useMemo(() => {
    const set = new Set();
    products.forEach(p => { if (p.category) set.add(p.category); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [products]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = products.filter(p => {
      if (categoryFilter && categoryFilter !== "__all__" && p.category !== categoryFilter) return false;
      if (!q) return true;
      return (p.name || '').toLowerCase().includes(q)
        || (p.category || '').toLowerCase().includes(q)
        || (p.sku || '').toLowerCase().includes(q);
    });
    // Tri : privilégie created_at (ISO string), fallback updated_at pour les vieux docs sans created_at
    const getDate = (p) => p.created_at || p.updated_at || '';
    const nameCollator = new Intl.Collator('fr', { sensitivity: 'base' });
    if (sortBy === 'newest') list.sort((a, b) => getDate(b).localeCompare(getDate(a)));
    else if (sortBy === 'oldest') list.sort((a, b) => getDate(a).localeCompare(getDate(b)));
    else if (sortBy === 'name_asc') list.sort((a, b) => nameCollator.compare(a.name || '', b.name || ''));
    else if (sortBy === 'name_desc') list.sort((a, b) => nameCollator.compare(b.name || '', a.name || ''));
    return list;
  }, [products, search, categoryFilter, sortBy]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search, categoryFilter, sortBy]);

  // Sélection
  const allOnPageSelected = pageItems.length > 0 && pageItems.every(p => selected.has(p.product_id));
  const someOnPageSelected = pageItems.some(p => selected.has(p.product_id));

  const toggleOne = (pid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };
  const toggleAllOnPage = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        pageItems.forEach(p => next.delete(p.product_id));
      } else {
        pageItems.forEach(p => next.add(p.product_id));
      }
      return next;
    });
  };
  const selectAllFiltered = () => {
    setSelected(new Set(filtered.map(p => p.product_id)));
  };
  const clearSelection = () => setSelected(new Set());

  // Bulk regen polling
  useEffect(() => {
    if (!bulkJob || bulkJob.status === 'done' || bulkJob.status === 'failed') {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/products/regen-job/${bulkJob.job_id}`, { withCredentials: true });
        setBulkJob(prev => ({ ...prev, ...res.data }));
        if (res.data.status === 'done') {
          toast.success(`Régénération terminée — ${res.data.done} produits, ${res.data.errors} erreurs`);
          fetchAll();
        }
      } catch (e) { /* silent */ }
    }, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [bulkJob]);

  const startBulkRegen = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (ids.length > 200) {
      toast.error("Maximum 200 produits par lot. Décompose en plusieurs sélections.");
      return;
    }
    const cost = (ids.length * 0.02).toFixed(2);
    if (!window.confirm(`Régénérer la description IA pour ${ids.length} produit(s) ?\n\nLes textes actuels seront remplacés.\nCoût LLM estimé : ~${cost}€ · Durée : ~${Math.ceil(ids.length * 8 / 60)} min.\n\nContinuer ?`)) return;
    try {
      const res = await axios.post(`${API}/products/bulk-regenerate-descriptions`, { product_ids: ids }, { withCredentials: true });
      setBulkJob({ job_id: res.data.job_id, total: res.data.total, done: 0, errors: 0, status: 'running' });
      toast.success("Régénération lancée en arrière-plan");
      clearSelection();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const openEdit = async (product) => {
    setEditing(product.product_id);
    try {
      const res = await axios.get(`${API}/products/${product.product_id}`);
      const full = res.data || product;
      setEditForm({
        name: full.name || '',
        description: full.description || '',
        short_description: full.short_description || '',
        price: full.price || 0,
        category: full.category || '',
        is_featured: !!full.is_featured,
        meta_title: full.seo?.meta_title || '',
        meta_description: full.seo?.meta_description || '',
        keywords: (full.seo?.keywords || []).join(', '),
      });
    } catch (e) {
      toast.error("Erreur de chargement du produit");
      setEditing(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/products/${editing}`, {
        name: editForm.name,
        description: editForm.description,
        short_description: editForm.short_description,
        price: parseFloat(editForm.price) || 0,
        category: (editForm.category || '').trim(),
        is_featured: !!editForm.is_featured,
        meta_title: editForm.meta_title,
        meta_description: editForm.meta_description,
        keywords: editForm.keywords.split(',').map(k => k.trim()).filter(Boolean),
      }, { withCredentials: true });
      toast.success("Produit mis à jour");
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm("Régénérer la description IA va remplacer le texte actuel. Continuer ?")) return;
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/products/${editing}/regenerate-description`, {}, { withCredentials: true });
      setEditForm(prev => ({
        ...prev,
        description: res.data.description || prev.description,
        short_description: res.data.short_description || prev.short_description,
        meta_title: res.data.seo?.meta_title || prev.meta_title,
        meta_description: res.data.seo?.meta_description || prev.meta_description,
        keywords: (res.data.seo?.keywords || []).join(', '),
      }));
      toast.success("Description IA régénérée (déjà sauvegardée).");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setRegenerating(false);
    }
  };

  if (editing) {
    return (
      <Card className="bg-[#261B3B] border-white/10" data-testid="admin-products-edit">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Package className="w-5 h-5 text-[#D926A9]" /> Modifier le produit
            </CardTitle>
            <button onClick={() => setEditing(null)} className="text-[#B59ED1] text-sm hover:text-white">← Retour à la liste</button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#B59ED1] mb-1 block">Nom</label>
              <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="bg-[#1B132B] border-white/10" />
            </div>
            <div>
              <label className="text-xs text-[#B59ED1] mb-1 block">Prix (€)</label>
              <Input type="number" step="0.01" value={editForm.price} onChange={(e) => setEditForm({...editForm, price: e.target.value})} className="bg-[#1B132B] border-white/10" />
            </div>
          </div>

          <div>
            <label className="text-xs text-[#B59ED1] mb-1 block">Catégorie boutique</label>
            <Input
              list="product-category-list"
              value={editForm.category}
              onChange={(e) => setEditForm({...editForm, category: e.target.value})}
              placeholder="Choisis une catégorie existante ou saisis-en une nouvelle"
              className="bg-[#1B132B] border-white/10"
              data-testid="product-category-input"
            />
            <datalist id="product-category-list">
              {categories.map(cat => <option key={cat} value={cat} />)}
            </datalist>
            <p className="text-[10px] text-[#B59ED1] mt-1">Détermine le filtre et le badge affichés dans la boutique. Laisse vide pour « sans catégorie ».</p>
          </div>

          <button
            type="button"
            onClick={() => setEditForm({...editForm, is_featured: !editForm.is_featured})}
            className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border-2 transition-all ${editForm.is_featured ? 'border-[#D926A9] bg-[#D926A9]/15' : 'border-white/10 bg-[#1B132B] hover:border-white/30'}`}
            data-testid="product-featured-toggle"
          >
            <span className="flex items-center gap-2 text-left">
              <Heart className={`w-5 h-5 ${editForm.is_featured ? 'fill-[#D926A9] text-[#D926A9]' : 'text-[#B59ED1]'}`} />
              <span>
                <span className={`block text-sm font-medium ${editForm.is_featured ? 'text-[#D926A9]' : 'text-white'}`}>Coup de cœur</span>
                <span className="block text-[11px] text-[#B59ED1]">{editForm.is_featured ? 'Épinglé — affiché en premier dans la boutique' : 'Mettre en avant ce produit en tête de la boutique'}</span>
              </span>
            </span>
            <span className={`flex-shrink-0 w-11 h-6 rounded-full p-0.5 transition-colors ${editForm.is_featured ? 'bg-[#D926A9]' : 'bg-white/20'}`}>
              <span className={`block w-5 h-5 rounded-full bg-white transition-transform ${editForm.is_featured ? 'translate-x-5' : ''}`} />
            </span>
          </button>

          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-xs text-[#B59ED1]">Description complète (Markdown)</label>
              <Button onClick={handleRegenerate} disabled={regenerating} variant="outline" size="sm" className="border-[#D926A9]/40 text-[#D926A9] hover:bg-[#D926A9]/10 text-xs" data-testid="regen-desc-btn">
                {regenerating ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Génération...</> : <><Sparkles className="w-3 h-3 mr-1" /> Régénérer avec l'IA</>}
              </Button>
            </div>
            <Textarea value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} className="bg-[#1B132B] border-white/10 min-h-[280px] font-mono text-xs leading-relaxed" data-testid="product-desc-textarea" />
            <p className="text-[10px] text-[#B59ED1] mt-1">Sections recommandées : Présentation · ✨ Bienfaits · 🌙 Comment l'utiliser · 💫 Pour qui ? · ❓ FAQ · 🔮 Rituel complémentaire.</p>
          </div>

          <div>
            <label className="text-xs text-[#B59ED1] mb-1 block">Description courte (affichée sur les cartes produits)</label>
            <Textarea value={editForm.short_description} onChange={(e) => setEditForm({...editForm, short_description: e.target.value})} className="bg-[#1B132B] border-white/10 min-h-[60px]" />
          </div>

          <div className="pt-3 border-t border-white/10">
            <p className="text-xs uppercase tracking-wider text-[#00E5FF] mb-2">Référencement SEO</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Meta title (50-60 car.)</label>
                <Input value={editForm.meta_title} onChange={(e) => setEditForm({...editForm, meta_title: e.target.value})} className="bg-[#1B132B] border-white/10" maxLength={70} />
              </div>
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Mots-clés (séparés par virgule)</label>
                <Input value={editForm.keywords} onChange={(e) => setEditForm({...editForm, keywords: e.target.value})} className="bg-[#1B132B] border-white/10" placeholder="améthyste, pierre, protection" />
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs text-[#B59ED1] mb-1 block">Meta description (150-160 car.)</label>
              <Textarea value={editForm.meta_description} onChange={(e) => setEditForm({...editForm, meta_description: e.target.value})} className="bg-[#1B132B] border-white/10 min-h-[60px]" maxLength={180} />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} className="gradient-primary" data-testid="product-save-btn">{saving ? 'Enregistrement...' : 'Enregistrer'}</Button>
            <Button onClick={() => setEditing(null)} variant="outline" className="border-white/10">Annuler</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#261B3B] border-white/10" data-testid="admin-products-manager">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <Package className="w-5 h-5 text-[#D926A9]" /> Produits ({filtered.length}{filtered.length !== products.length ? ` / ${products.length}` : ''})
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/admin/products/duplicates`, { withCredentials: true });
                  const groups = res.data?.groups || [];
                  if (groups.length === 0) {
                    toast.success("Aucun doublon détecté ✨");
                    return;
                  }
                  const total = groups.reduce((s, g) => s + (g.duplicates?.length || 0), 0);
                  const preview = groups.slice(0, 5).map(g => `• ${g.keep.name} (SKU ${g.keep.sku || '-'}) → ${g.duplicates.length} doublon(s)`).join('\n');
                  const more = groups.length > 5 ? `\n…et ${groups.length - 5} autre(s) groupe(s)` : '';
                  const ok = window.confirm(
                    `${groups.length} groupe(s) de doublons détecté(s) (${total} produit(s) à archiver) :\n\n${preview}${more}\n\nLes doublons seront désactivés (les commandes historiques restent intactes). Confirmer ?`
                  );
                  if (!ok) return;
                  const dr = await axios.post(`${API}/admin/products/dedupe`, {}, { withCredentials: true });
                  toast.success(`${dr.data.deactivated_count} doublon(s) archivé(s) ✨`);
                  await fetchAll();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Erreur lors de la détection des doublons");
                }
              }}
              variant="outline"
              size="sm"
              className="border-orange-500/30 text-orange-300 hover:bg-orange-500/10"
              data-testid="products-dedupe-btn"
              title="Détecter et archiver les doublons (même SKU ou même square_item_id)"
            >
              🧹 Dédoublonner
            </Button>
            <Button onClick={fetchAll} disabled={loading} variant="outline" size="sm" className="border-white/10 text-[#B59ED1]">
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualiser
            </Button>
          </div>
        </div>

        {/* Filtres */}
        <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_280px] gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#B59ED1]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom, catégorie ou SKU..."
              className="pl-9 bg-[#1B132B] border-white/10"
              data-testid="product-search-input"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="bg-[#1B132B] border-white/10" data-testid="product-category-filter">
              <SelectValue placeholder="Toutes les catégories" />
            </SelectTrigger>
            <SelectContent className="bg-[#1B132B] border-white/10 text-white max-h-[400px]">
              <SelectItem value="__all__">Toutes les catégories ({products.length})</SelectItem>
              {categories.map(cat => {
                const count = products.filter(p => p.category === cat).length;
                return (
                  <SelectItem key={cat} value={cat}>{cat} ({count})</SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="bg-[#1B132B] border-white/10 sm:w-[200px]" data-testid="product-sort-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1B132B] border-white/10 text-white">
              <SelectItem value="newest">Nouveautés (récents en 1er)</SelectItem>
              <SelectItem value="oldest">Plus anciens en 1er</SelectItem>
              <SelectItem value="name_asc">Nom A → Z</SelectItem>
              <SelectItem value="name_desc">Nom Z → A</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Job de régénération en cours */}
        {bulkJob && bulkJob.status !== 'failed' && (
          <div className="mt-4 p-3 rounded-lg bg-gradient-to-r from-[#D926A9]/15 to-[#9D4CDD]/15 border border-[#D926A9]/30" data-testid="bulk-job-progress">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-white flex items-center gap-2">
                {bulkJob.status === 'done' ? <Sparkles className="w-4 h-4 text-[#D926A9]" /> : <Loader2 className="w-4 h-4 text-[#D926A9] animate-spin" />}
                {bulkJob.status === 'done' ? 'Régénération terminée' : 'Régénération IA en cours…'}
              </p>
              <span className="text-xs text-[#B59ED1]">{bulkJob.done}/{bulkJob.total}{bulkJob.errors > 0 && ` · ${bulkJob.errors} erreurs`}</span>
            </div>
            <div className="w-full h-2 rounded-full bg-[#1B132B] overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#D926A9] to-[#9D4CDD] transition-all" style={{ width: `${(bulkJob.done / Math.max(1, bulkJob.total)) * 100}%` }} />
            </div>
            {bulkJob.current_name && bulkJob.status !== 'done' && (
              <p className="text-[10px] text-[#B59ED1] mt-1.5 truncate">En cours : {bulkJob.current_name}</p>
            )}
            {bulkJob.status === 'done' && (
              <button onClick={() => setBulkJob(null)} className="text-[10px] text-[#B59ED1] hover:text-white mt-1.5">Masquer</button>
            )}
          </div>
        )}

        {/* Barre d'actions sélection */}
        {selected.size > 0 && (
          <div className="mt-3 p-3 rounded-lg bg-[#1B132B] border border-[#D926A9]/30 flex items-center justify-between flex-wrap gap-3" data-testid="bulk-action-bar">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-[#D926A9]/20 text-[#D926A9]">{selected.size} sélectionné·s</Badge>
              {filtered.length > pageItems.length && selected.size < filtered.length && (
                <button onClick={selectAllFiltered} className="text-xs text-[#00E5FF] hover:underline">
                  Sélectionner les {filtered.length} produits filtrés
                </button>
              )}
              <button onClick={clearSelection} className="text-xs text-[#B59ED1] hover:text-white flex items-center gap-1">
                <X className="w-3 h-3" /> Désélectionner
              </button>
            </div>
            <Button onClick={startBulkRegen} disabled={!!bulkJob && bulkJob.status === 'running'} className="gradient-primary text-xs" data-testid="bulk-regen-btn">
              <Sparkles className="w-3 h-3 mr-1" /> Régénérer les descriptions IA ({selected.size})
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-12 text-center text-[#B59ED1]"><Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />Chargement...</div>
        ) : pageItems.length === 0 ? (
          <p className="py-8 text-center text-[#B59ED1]">Aucun produit trouvé.</p>
        ) : (
          <div className="space-y-2">
            {/* Sélecteur "tout cocher sur la page" */}
            <label className="flex items-center gap-3 px-3 py-2 text-xs text-[#B59ED1] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allOnPageSelected}
                ref={el => { if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected; }}
                onChange={toggleAllOnPage}
                className="w-4 h-4 rounded accent-[#D926A9]"
                data-testid="select-all-checkbox"
              />
              <span>{allOnPageSelected ? 'Tout désélectionner sur cette page' : 'Tout sélectionner sur cette page'} ({pageItems.length})</span>
            </label>

            {pageItems.map(p => {
              const isSel = selected.has(p.product_id);
              return (
                <div key={p.product_id} className={`p-3 rounded-lg flex items-center gap-3 transition-colors ${isSel ? 'bg-[#D926A9]/10 border border-[#D926A9]/30' : 'bg-[#1B132B] border border-transparent'}`} data-testid={`product-row-${p.product_id}`}>
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => toggleOne(p.product_id)}
                    className="w-4 h-4 rounded accent-[#D926A9] shrink-0"
                    data-testid={`product-check-${p.product_id}`}
                  />
                  {p.image_url ? (
                    <img src={p.image_url.startsWith('/') ? `${BACKEND_URL}${p.image_url}` : p.image_url} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-[#3A115E] flex items-center justify-center flex-shrink-0"><Sparkles className="w-5 h-5 text-[#D926A9]/60" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className="font-medium text-sm truncate text-[#F7F3FC]">{p.name}</h4>
                      <Badge className="text-[10px] bg-[#3A115E] text-[#E2D5F8]">{p.category}</Badge>
                      {p.is_featured && <Badge className="text-[10px] bg-[#D926A9]/20 text-[#D926A9] flex items-center gap-1"><Heart className="w-3 h-3 fill-[#D926A9]" />Coup de cœur</Badge>}
                      {(p.stock || 0) <= 0 && <Badge className="text-[10px] bg-red-500/20 text-red-400">Rupture</Badge>}
                    </div>
                    <p className="text-[#B59ED1] text-xs">{(p.price || 0).toFixed(2)} € · stock {p.stock || 0}{p.sku ? ` · ${p.sku}` : ''}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <a href={`/boutique/${p.product_id}`} target="_blank" rel="noreferrer" className="p-2 text-[#B59ED1] hover:text-white" title="Voir la fiche publique"><ExternalLink className="w-3 h-3" /></a>
                    <Button variant="ghost" size="sm" className="text-[#00E5FF]" onClick={() => openEdit(p)} data-testid={`edit-product-${p.product_id}`}>Modifier</Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {pageCount > 1 && (
          <div className="flex justify-center items-center gap-3 mt-4">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-white/10">← Précédent</Button>
            <span className="text-xs text-[#B59ED1]">Page {page} / {pageCount}</span>
            <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage(p => p + 1)} className="border-white/10">Suivant →</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
